import React from 'react';

export interface ArrayActionItem<T> {
  item: T;
  replace: (c: T | ((old: T) => T)) => void;
  remove: () => void;
}

export interface ArrayActions<T> {
  append: (c: T) => void;
  prepend: (c: T) => void;
  concat: (cs: T[]) => void;
  insertAt: (i: number, cs: T[]) => void;
  reorder: (source: number, dest: number) => void;
}

// TODO: see if we can reuse in memoising callbacks in the config panel
export function useArrayActions<T>(
  arr: T[],
  setArr: (f: (xs: T[]) => T[]) => void
): [ArrayActionItem<T>[], ArrayActions<T>] {
  const actions = React.useMemo(() => {
    return {
      prepend: (c: T) => setArr(a => [c].concat(a)),
      append: (c: T) => setArr(a => a.concat([c])),
      concat: (c: T[]) => setArr(a => a.concat(c)),
      insertAt: (i: number, c: T[]) =>
        setArr(a => {
          const res = a.slice();
          res.splice(i, 0, ...c);
          return res;
        }),
      reorder: (source:number, dest: number) => {
        if (source === dest) return;
        setArr(a => {
          const res = a.slice();
          const [removed] = res.splice(source, 1);
          res.splice(dest, 0, removed)
          return res;
        });
      },
    };
  }, [setArr]);
  const items = React.useMemo(() => {
    return arr.map((item, i) => ({
      item,
      replace: (c: T | ((old: T) => T)) =>
        setArr(a => {
          const res = a.slice();
          const n = isUpdaterFunction(c) ? c(res[i]) : c;
          res.splice(i, 1, n);
          return res;
        }),
      remove: () =>
        setArr(a => {
          const res = a.slice();
          res.splice(i, 1);
          return res;
        }),
    }));
  }, [arr, setArr]);

  return [items, actions];
}

function isUpdaterFunction<T>(
  maybe: T | ((old: T) => T)
): maybe is (old: T) => T {
  if (typeof maybe === 'function') return true;
  return false;
}
